import React, { useEffect, useId, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { reconcileGiftDrinks, sumGiftDrinks } from "../../utils/orderGiftDrinks";
import "./OrderGiftDrinkPicker.css";

export function OrderGiftDrinkRows({ items }) {
  return items.map((item) => (
    <div className="order-item order-gift-item" role="group" aria-label={`Nước tặng ${item.name}`} key={`gift-${item.id}`}>
      <div className="item-info">
        <h5>{item.name} <span className="badge bg-success">Tặng</span></h5>
        <p className="item-price">Miễn phí · 0₫ × {item.quantity}</p>
      </div>
      <div className="item-controls">
        <span className="item-total">0₫</span>
        <span className="quantity" aria-label={`Số lượng nước tặng ${item.name}`}>{item.quantity}</span>
      </div>
    </div>
  ));
}

export default function OrderGiftDrinkPicker({ gifts, disabled, onCheckout }) {
  const [open, setOpen] = useState(false);
  const checkoutAfterClose = useRef(false);
  const titleId = useId();
  const hasActivePromotion = gifts.availablePromotions.some((promotion) =>
    [9, 13].includes(Number(promotion.MaUD)));
  const canShowPicker = !gifts.loading && hasActivePromotion && gifts.limit >= 1;
  useEffect(() => {
    if (!canShowPicker) {
      checkoutAfterClose.current = false;
      setOpen(false);
    }
  }, [canShowPicker]);
  const closeMenu = () => {
    checkoutAfterClose.current = false;
    setOpen(false);
  };
  const closeAndCheckout = () => {
    if (disabled || !onCheckout) return;
    checkoutAfterClose.current = true;
    setOpen(false);
  };
  const handleExited = () => {
    if (!checkoutAfterClose.current) return;
    checkoutAfterClose.current = false;
    onCheckout?.();
  };
  if (!canShowPicker) return null;
  return (
    <div className="order-gift-drinks" onKeyDown={(event) => event.stopPropagation()}>
      <Button variant="outline-primary" size="lg" className="order-gift-drinks-button"
        aria-label={`Chọn nước tặng (${gifts.quantity}/${gifts.limit})`}
        onClick={() => setOpen(true)} disabled={disabled}>
        Chọn nước tặng ({gifts.quantity}/{gifts.limit})
      </Button>
      <Modal show={open} onHide={closeMenu} onExited={handleExited} centered size="lg"
        restoreFocus={!checkoutAfterClose.current}
        dialogClassName="order-gift-drink-modal"
        onKeyDown={(event) => event.stopPropagation()}
        aria-labelledby={titleId}>
        <Modal.Header closeButton>
          <div className="order-gift-drink-heading">
            <span className="order-gift-drink-icon" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 8h10v7a5 5 0 0 1-10 0V8Z" />
                <path d="M16 9h1.5a2.5 2.5 0 0 1 0 5H16M9 3v2m4-2v2M4 21h16" />
              </svg>
            </span>
            <div>
              <span className="order-gift-drink-eyebrow">MỘT CHÚT QUÀ, THÊM NIỀM VUI</span>
              <Modal.Title id={titleId}>Chọn nước tặng</Modal.Title>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body onKeyDown={(event) => event.stopPropagation()}>
          <div className="order-gift-drink-summary" aria-live="polite">
            <div className="order-gift-drink-summary-line">
              <strong>Đã chọn {gifts.quantity}/{gifts.limit} phần nước tặng</strong>
              <span className="order-gift-drink-remaining">Còn {gifts.limit - gifts.quantity} phần</span>
            </div>
            <div className="order-gift-drink-progress" role="progressbar" aria-label="Số phần nước đã chọn"
              aria-valuemin={0} aria-valuemax={gifts.limit || 1} aria-valuenow={gifts.quantity}>
              <span style={{ width: `${gifts.limit ? Math.min(100, gifts.quantity / gifts.limit * 100) : 0}%` }} />
            </div>
          </div>
          <p className="order-gift-drink-rule">Tiền bánh: <strong>{gifts.allowance[13]} phần</strong><span aria-hidden="true"> · </span>Bánh mì có nhân từ 20.000₫: <strong>{gifts.allowance[9]} phần</strong></p>
          {gifts.error && <div className="alert alert-danger" role="alert">{gifts.error}</div>}
          {gifts.loading ? <p role="status">Đang tải menu nước tặng...</p> : <>
            {gifts.limit === 0 && <p>Order chưa đủ điều kiện hoặc cửa hàng không có ưu đãi nước tặng đang khả dụng.</p>}
            {gifts.options.length === 0 && <p>Không có nước tặng để chọn.</p>}
            <div className="order-gift-drink-grid">
              {gifts.options.map((option) => {
                const key = String(option.id);
                const quantity = gifts.selected[key] || 0;
                const candidate = { ...gifts.selected, [key]: quantity + 1 };
                const canAdd = sumGiftDrinks(reconcileGiftDrinks(candidate, gifts.options, gifts.allowance)) === gifts.quantity + 1;
                return <div className={`order-gift-drink-card${quantity ? " is-selected" : ""}`} key={key}>
                  <button type="button" className="order-gift-drink-card-add" aria-label={`Chọn ${option.name}`}
                    disabled={!canAdd || disabled} onClick={() => gifts.changeQuantity(key, quantity + 1)}>
                    <span className="order-gift-drink-card-meta">
                      <span>NƯỚC TẶNG</span>
                      {quantity > 0 && <span className="order-gift-drink-selected" aria-hidden="true">✓ {quantity}</span>}
                    </span>
                    <strong>{option.name}</strong>
                    <span className="order-gift-drink-free">Miễn phí · 0₫</span>
                    <span className="small text-muted">{canAdd ? "Chạm để thêm" : "Đã đủ suất nước"}</span>
                  </button>
                  <div className="order-gift-drink-controls d-flex align-items-center gap-2 mt-2">
                    <Button variant="outline-secondary" aria-label={`Giảm ${option.name}`} disabled={!quantity || disabled}
                      onClick={() => gifts.changeQuantity(key, quantity - 1)}>−</Button>
                    <output aria-label={`Số lượng ${option.name}`}>{quantity}</output>
                    <Button variant="outline-primary" aria-label={`Thêm ${option.name}`} disabled={!canAdd || disabled}
                      onClick={() => gifts.changeQuantity(key, quantity + 1)}>+</Button>
                  </div>
                </div>;
              })}
            </div>
          </>}
        </Modal.Body>
        <Modal.Footer className="order-gift-drink-footer" role="group" aria-label="Thao tác chọn nước"
          onKeyDown={(event) => event.stopPropagation()}>
          <Button variant="outline-danger" className="order-gift-drink-clear" disabled={disabled || gifts.loading || !gifts.quantity} onClick={gifts.clear}>Bỏ chọn nước</Button>
          <Button variant="outline-secondary" onClick={closeMenu}>Đóng</Button>
          <Button variant="success" disabled={disabled || gifts.loading || !onCheckout} onClick={closeAndCheckout}>
            Đóng và thanh toán <span aria-hidden="true">→</span>
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
